import { useCallback, useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import Spinner from '../../../../components/template/Spinner';
import Modal from '../../../../components/template/Modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRotate } from '@fortawesome/free-solid-svg-icons';
import api from '../../../../components/utils/api';
import ModelInfoModal from './ModelInfoModal';

const DirectLink = ({ path, label }) => (
    <a
        href={path}
        target='_blank'
        rel="noopener noreferrer"
        title="redirect to the converted model"
    >
        <span className='font-medium inline-flex items-center justify-center h-12 w-40 text-sm text-violet-500 bg-violet-100 border border-violet-400 rounded hover:bg-violet-200 m-1'>
            {label}
        </span>
    </a>
)

const ConvertBtnTemplate = ({ label, onClick, disabled, title = "", variant = "default" }) => {
    const styles = {
        default: 'text-violet-500 bg-violet-100 border-violet-400 hover:bg-violet-200 disabled:border-gray-300 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed',
        failed: 'text-red-500 bg-red-50 border-red-400 hover:bg-red-100',
    }
    return (
        <button
            type='button'
            className={`font-medium h-12 w-40 m-1 text-sm border rounded flex items-center justify-center ${styles[variant]}`}
            onClick={onClick}
            disabled={disabled}
            title={title}
        >{label}</button>
    )
}


const getAvailableModels = (modelData) => {
    if (!modelData?.available_models) {
        return { all: true, pre: true, main: true };
    }
    return {
        all: modelData.available_models.includes('all'),
        pre: modelData.available_models.includes('pre'),
        main: modelData.available_models.includes('main'),
    };
};

const isInspectable = (modelData, isTorch) => {
    if (!modelData) return false
    if (isTorch) {
        return modelData.main_status === 'completed' || modelData.all_status === 'completed'
    }
    return modelData.status === 5
}

const ConvertPage = () => {
    const [convertedModels, setConvertedModels] = useState({})
    const [convertingKeys, setConvertingKeys] = useState(new Set())
    const [generalModel, setGeneralModel] = useState({})
    const [inspectTarget, setInspectTarget] = useState(null)
    const { generalModelId, mergeRuleId } = useParams();

    const isTorch = useMemo(() => generalModel?.model_type === "Torch", [generalModel?.model_type])

    const groupByFramework = (convertedModels) => {
        const grouped = {}
        Object.entries(convertedModels).forEach(([key, value]) => {
            const [framework, dataType] = key.split('_')
            if (!grouped[framework]) {
                grouped[framework] = {}
            }
            grouped[framework][dataType] = {
                ...value,
                fullKey: key
            }
        })
        return grouped
    }

    const fetchConvertedModels = useCallback(async () => {
        try {
            const res = await api.get(`/api/merge-rules/${mergeRuleId}/converted-models/framework-map?is_torch=${isTorch}`)
            const newModels = res.data.converted_model_map
            setConvertedModels({ ...newModels })
            setConvertingKeys(prev => {
                if (prev.size === 0) return prev
                const next = new Set(prev)
                for (const key of prev) {
                    if (key.includes(':')) {
                        // Torch key ("fullKey:buttonType") — status is tracked via JSX conditions
                        next.delete(key)
                    } else {
                        // Legacy key — only clear if server status has moved past initial state
                        const model = newModels[key]
                        if (!model || (model.status !== undefined && model.status !== 0)) {
                            next.delete(key)
                        }
                    }
                }
                return next
            })
        } catch (err) {
            console.error(err)
        }
    }, [mergeRuleId, isTorch])

    const handleConvert = async (convertModel) => {
        setConvertingKeys(prev => new Set(prev).add(convertModel))
        const [framework, dataType] = convertModel.split("_")
        try {
            await api.post(`/api/merge-rules/${mergeRuleId}/converted-models`, {
                framework,
                data_type: dataType,
                is_torch: isTorch
            })
            await fetchConvertedModels()
        } catch (err) {
            console.error(err)
            setConvertingKeys(prev => {
                const next = new Set(prev)
                next.delete(convertModel)
                return next
            })
        }
    }

    const handleTorchConvert = async (convertModel, modelsToGenerate, buttonType) => {
        const convertingKey = `${convertModel}:${buttonType}`
        setConvertingKeys(prev => new Set(prev).add(convertingKey))
        const [framework, dataType] = convertModel.split("_")
        try {
            await api.post(`/api/merge-rules/${mergeRuleId}/converted-models`, {
                framework,
                data_type: dataType,
                models_to_generate: modelsToGenerate,
                is_torch: isTorch
            })
            await fetchConvertedModels()
        } catch (err) {
            console.error(err)
            setConvertingKeys(prev => {
                const next = new Set(prev)
                next.delete(convertingKey)
                return next
            })
        }
    }

    useEffect(() => {
        if (mergeRuleId && generalModel) {
            fetchConvertedModels()
        }
    }, [mergeRuleId, generalModel, fetchConvertedModels])

    useEffect(() => {
        const fetchGeneralModel = async () => {
            try {
                const res = await api.get(`/api/general-models/${generalModelId}`)
                setGeneralModel(res.data)
            } catch (err) {
                console.error(err)
            }
        }
        if (generalModelId) {
            fetchGeneralModel()
        }
    }, [generalModelId, fetchConvertedModels])

    return (
        <div className='p-1'>
            <div className='text-right m-1'>
                <button type='button' className='text-sm text-gray-400' onClick={() => fetchConvertedModels()}>Refresh <FontAwesomeIcon icon={faRotate} /></button>
            </div>
            <div>
                {Object.entries(groupByFramework(convertedModels)).map(([framework, dataTypes]) => (
                    <div key={framework} className='mb-3'>
                        <div className='font-bold text-base mb-1 text-left'>{framework}</div>
                        {Object.entries(dataTypes).map(([dataType, modelData]) => {
                            const available = getAvailableModels(modelData);
                            const allModelsToGenerate = available.pre ? ['all', 'pre'] : ['all'];
                            const mainModelsToGenerate = available.pre ? ['main', 'pre'] : ['main'];
                            return (
                            <div key={dataType} className='md:flex md:justify-between items-center text-left pl-6'>
                                <div className='font-medium text-gray-700'>{dataType}</div>
                                <div className='flex items-center'>
                                    {/* For Torch models: 2 buttons (All may be disabled for INT8/EdgeTPU) */}
                                    {isTorch ? (
                                        <>
                                            {/* Button 1: Convert All (pre + all) */}
                                            {(dataType === 'int8' || framework === 'edgetpu' || !available.all) ? (
                                                <ConvertBtnTemplate label={<div>
                                                    <div className='font-medium'>Not Supported</div>
                                                    <div className='text-xs text-inherit'>All</div>
                                                </div>} onClick={() => { }} disabled={true}
                                                    title="All model is not supported for this configuration." />
                                            ) : (modelData.all_status === 'completed' && (modelData.pre_status === 'completed' || !available.pre)) ? (
                                                <ConvertBtnTemplate label={<div>
                                                    <div className='font-medium'>Converted</div>
                                                    <div className='text-xs text-inherit'>All</div>
                                                </div>} onClick={() => { }} disabled={true} />
                                            ) : (modelData.all_status === 'failed' || (available.pre && modelData.pre_status === 'failed')) ? (
                                                <ConvertBtnTemplate variant="failed" label={<div>
                                                    <div className='font-medium'>Failed <FontAwesomeIcon icon={faRotate} className='text-xs' /></div>
                                                    <div className='text-xs text-inherit'>All</div>
                                                </div>} onClick={() => handleTorchConvert(modelData.fullKey, allModelsToGenerate, 'all')} disabled={false}
                                                    title={modelData.status_message || ''} />
                                            ) : convertingKeys.has(modelData.fullKey + ':all') || ['pending', 'in_progress'].includes(modelData.all_status) || (available.pre && ['pending', 'in_progress'].includes(modelData.pre_status)) ? (
                                                <ConvertBtnTemplate label={<div className='flex m-auto'>
                                                    <div className='pt-0.5 pl-2'><Spinner /></div>
                                                    <div className='m-auto cursor-default text-xs'>{modelData.status_message || 'Converting...'}</div>
                                                </div>} onClick={() => { }} disabled={true} />
                                            ) : (
                                                <ConvertBtnTemplate
                                                    label={<div>
                                                        <div className='font-medium'>Convert</div>
                                                        <div className='text-xs text-inherit'>All</div>
                                                    </div>}
                                                    onClick={() => handleTorchConvert(modelData.fullKey, allModelsToGenerate, 'all')}
                                                    disabled={convertingKeys.has(modelData.fullKey + ':main')}
                                                />
                                            )}

                                            {/* Button 2: Convert Main */}
                                            {!available.main ? (
                                                <ConvertBtnTemplate label={<div>
                                                    <div className='font-medium'>Not Supported</div>
                                                    <div className='text-xs text-inherit'>Main</div>
                                                </div>} onClick={() => { }} disabled={true}
                                                    title="Main model is not available for this architecture." />
                                            ) : modelData.main_status === 'completed' ? (
                                                <ConvertBtnTemplate label={<div>
                                                    <div className='font-medium'>Converted</div>
                                                    <div className='text-xs text-inherit'>Main</div>
                                                </div>} onClick={() => { }} disabled={true} />
                                            ) : modelData.main_status === 'failed' ? (
                                                <ConvertBtnTemplate variant="failed" label={<div>
                                                    <div className='font-medium'>Failed <FontAwesomeIcon icon={faRotate} className='text-xs' /></div>
                                                    <div className='text-xs text-inherit'>Main</div>
                                                </div>} onClick={() => handleTorchConvert(modelData.fullKey, mainModelsToGenerate, 'main')} disabled={false}
                                                    title={modelData.status_message || ''} />
                                            ) : convertingKeys.has(modelData.fullKey + ':main') || ['pending', 'in_progress'].includes(modelData.main_status) ? (
                                                <ConvertBtnTemplate label={<div className='flex m-auto'>
                                                    <div className='pt-0.5 pl-2'><Spinner /></div>
                                                    <div className='m-auto cursor-default text-xs'>{modelData.status_message || 'Converting...'}</div>
                                                </div>} onClick={() => { }} disabled={true} />
                                            ) : (
                                                <ConvertBtnTemplate
                                                    label={<div>
                                                        <div className='font-medium'>Convert</div>
                                                        <div className='text-xs text-inherit'>Main</div>
                                                    </div>}
                                                    onClick={() => handleTorchConvert(modelData.fullKey, mainModelsToGenerate, 'main')}
                                                    disabled={convertingKeys.has(modelData.fullKey + ':all')}
                                                />
                                            )}
                                        </>
                                    ) : (
                                        /* Legacy UI (non-Torch models) */
                                        <>
                                            {convertingKeys.has(modelData.fullKey)
                                                ? <ConvertBtnTemplate label={<div className='flex m-auto'>
                                                    <div className='pt-0.5 pl-2'><Spinner /></div>
                                                    <div className='m-auto cursor-default text-xs'>Converting...</div>
                                                </div>} onClick={() => { }} disabled={true} />
                                                : (modelData.status === undefined || modelData.status === 0 || modelData.status === 5)
                                                    ? <ConvertBtnTemplate label={modelData.status === 5 ? "Converted" : "Convert"} onClick={() => handleConvert(modelData.fullKey)} disabled={modelData.status !== undefined && modelData.status !== 0} />
                                                    : <ConvertBtnTemplate label={<div className='flex m-auto'>
                                                        <div className='pt-0.5 pl-2'><Spinner /></div>
                                                        <div className='m-auto cursor-default text-xs'>Converting...</div>
                                                    </div>} onClick={() => { }} disabled={true} />
                                            }
                                        </>
                                    )}

                                    {/* Google Storage button */}
                                    {(modelData.status === undefined || modelData.status !== 5)
                                        ? (<ConvertBtnTemplate label='Google Storage' onClick={() => { }} disabled={true} title="To use this button, please convert the model first" />)
                                        : (<DirectLink path={`https://console.cloud.google.com/storage/browser/${modelData.path}`} label='Google Storage' />)
                                    }

                                    {/* Inspect Model button — opens modal showing framework, I/O specs, layer graph */}
                                    {isInspectable(modelData, isTorch)
                                        ? <ConvertBtnTemplate
                                            label='Inspect Model'
                                            onClick={() => setInspectTarget({ id: modelData.id, framework, dataType })}
                                            disabled={false}
                                        />
                                        : <ConvertBtnTemplate
                                            label='Inspect Model'
                                            onClick={() => { }}
                                            disabled={true}
                                            title='Convert the model first'
                                        />
                                    }
                                </div>
                            </div>
                        )})}
                    </div>
                ))}
            </div >
            {inspectTarget && (
                <Modal
                    visible={true}
                    closable={true}
                    maskClosable={true}
                    onClose={() => setInspectTarget(null)}
                >
                    <ModelInfoModal
                        convertedModelId={inspectTarget.id}
                        framework={inspectTarget.framework}
                        dataType={inspectTarget.dataType}
                    />
                </Modal>
            )}
        </div>
    )
}

export default ConvertPage